import { useEffect, useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Button, Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAuthStore, useStudioStore } from '@/store'
import { useCalendarMonthMetrics } from '@/hooks/useCalendarMonthMetrics'
import { MonthlyRevenueTable } from '@/components/finance/MonthlyRevenueTable'
import { MonthlyExpenseFactsEditor } from '@/components/finance/MonthlyExpenseFactsEditor'
import { buildFinanceExpenseGroups, FINANCE_MONTHLY_PILOT_USER_ID, type FinanceExpenseCategory, type FinanceMonthlyFactRow, type FinanceMonthlyFactValues } from '@/lib/financeMonthlyFacts'
import { getFinanceExpenseCategories, getFinanceMonthlyExpenseFacts, saveFinanceMonthlyExpenseFacts } from '@/services/financeMonthlyFactsService'
import type { Studio } from '@/types'

type CurrencyMode = 'local' | 'rub'

export function MonthlyRevenuePage() {
  const { studioId } = useParams<{ studioId: string }>()
  const studioInternalId = Number(studioId || 0)
  const { studios } = useStudioStore()
  const user = useAuthStore(state => state.user)
  const currentStudio = studios.find((studio) => studio.studio_internal_id === studioInternalId)

  // Базовый экран сохраняет прежний доступ. Пилотный гейт ниже включает только расходы.
  if (!currentStudio || currentStudio.role === 'member' || currentStudio.role === 'teacher' || currentStudio.role === 'senior_teacher') {
    return <Navigate to="/" replace />
  }

  const expensePilotEnabled = user?.id === FINANCE_MONTHLY_PILOT_USER_ID && currentStudio.access_kind === 'owner'
  return <MonthlyRevenueContent studioInternalId={studioInternalId} currentStudio={currentStudio} expensePilotEnabled={expensePilotEnabled} />
}

function MonthlyRevenueContent({ studioInternalId, currentStudio, expensePilotEnabled }: { studioInternalId: number; currentStudio: Studio; expensePilotEnabled: boolean }) {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState<number>(currentYear)
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('local')
  const [expenseRows, setExpenseRows] = useState<FinanceMonthlyFactRow[]>([])
  const [expenseCategories, setExpenseCategories] = useState<FinanceExpenseCategory[]>([])
  const [expenseLoading, setExpenseLoading] = useState(true)
  const [expenseError, setExpenseError] = useState<string | null>(null)
  const [expenseEditorOpen, setExpenseEditorOpen] = useState(false)
  const [expenseSaving, setExpenseSaving] = useState(false)
  const { rows, currencyCode, loading, error } = useCalendarMonthMetrics(
    Number.isFinite(studioInternalId) && studioInternalId > 0 ? studioInternalId : null,
    year
  )

  const selectedCurrency = currencyMode === 'rub' ? 'RUB' : (currencyCode || (currentStudio?.currency_code || 'RUB'))
  const expenseCurrency = currencyCode || currentStudio.currency_code || 'RUB'
  const canToggleCurrency = (currencyCode || currentStudio?.currency_code || 'RUB') !== 'RUB'

  useEffect(() => {
    if (!expensePilotEnabled) {
      setExpenseRows([])
      setExpenseCategories([])
      setExpenseLoading(false)
      setExpenseError(null)
      return
    }
    let cancelled = false
    setExpenseLoading(true)
    setExpenseError(null)
    void Promise.all([
      getFinanceMonthlyExpenseFacts(studioInternalId, year),
      getFinanceExpenseCategories(studioInternalId),
    ])
      .then(([data, categories]) => {
        if (!cancelled) {
          setExpenseRows(data)
          setExpenseCategories(categories)
        }
      })
      .catch(loadError => {
        if (!cancelled) {
          setExpenseRows([])
          setExpenseCategories([])
          setExpenseError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить расходы')
        }
      })
      .finally(() => { if (!cancelled) setExpenseLoading(false) })
    return () => { cancelled = true }
  }, [expensePilotEnabled, studioInternalId, year])

  const expenseGroups = useMemo(() => buildFinanceExpenseGroups(
    expenseCategories,
    new Set(expenseRows.flatMap(row => Object.keys(row.values)))
  ), [expenseCategories, expenseRows])

  const saveExpenseMonth = async (month: number, values: FinanceMonthlyFactValues, expectedVersion: number | null) => {
    setExpenseSaving(true)
    setExpenseError(null)
    try {
      const saved = await saveFinanceMonthlyExpenseFacts({
        studioInternalId,
        monthStart: `${year}-${String(month).padStart(2, '0')}-01`,
        values,
        expectedVersion,
      })
      setExpenseRows(current => [...current.filter(row => row.month_start !== saved.month_start), saved].sort((a, b) => a.month_start.localeCompare(b.month_start)))
    } catch (saveError) {
      setExpenseError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить расходы')
      throw saveError
    } finally {
      setExpenseSaving(false)
    }
  }

  const yearOptions = useMemo(() => {
    const dynamicYears = Array.from(new Set(rows.map((row) => row.year)))
    const presetYears = [currentYear - 1, currentYear, currentYear + 1]
    return Array.from(new Set([...presetYears, ...dynamicYears])).sort((a, b) => a - b)
  }, [rows, currentYear])

  if (loading) return <LoadingScreen />

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">ДДС / PnL</h2>
            <p className="text-sm text-muted-foreground">
              Календарные месяцы
              </p>
            <div className="mt-2 inline-flex items-center rounded-full border border-dashed border-primary/40 bg-primary/5 px-3 py-1 text-xs text-primary">
              Раздел в развитии: это первая версия, скоро добавим больше.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {expensePilotEnabled && <Button size="sm" onClick={() => setExpenseEditorOpen(true)}>Внести расходы</Button>}
            <select
              value={String(year)}
              onChange={(event) => setYear(Number(event.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {yearOptions.map((optionYear) => (
                <option key={optionYear} value={optionYear}>
                  {optionYear}
                </option>
              ))}
            </select>

            {canToggleCurrency && expensePilotEnabled && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Валюта доходов</span>
                <div className="inline-flex overflow-hidden rounded-md border border-input">
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm ${currencyMode === 'local' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                    onClick={() => setCurrencyMode('local')}
                  >
                    Местная
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm ${currencyMode === 'rub' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                    onClick={() => setCurrencyMode('rub')}
                  >
                    В рублях
                  </button>
                </div>
              </div>
            )}
            {canToggleCurrency && !expensePilotEnabled && (
              <div className="inline-flex overflow-hidden rounded-md border border-input">
                <button
                  type="button"
                  className={`px-3 py-2 text-sm ${currencyMode === 'local' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  onClick={() => setCurrencyMode('local')}
                >
                  Местная
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 text-sm ${currencyMode === 'rub' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  onClick={() => setCurrencyMode('rub')}
                >
                  В рублях
                </button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border border-red-300">
          <p className="text-sm text-red-600">{error}</p>
        </Card>
      )}

      <Card className="overflow-hidden !p-0">
        <MonthlyRevenueTable
          rows={rows}
          currencyMode={currencyMode}
          selectedCurrency={selectedCurrency}
          expenseGroups={expenseGroups}
          expenseRows={expenseRows}
          expenseCurrency={expenseCurrency}
          expenseLoading={expenseLoading}
          expenseError={expenseError}
          showExpenses={expensePilotEnabled}
          reserveExpenseEditorSpace={expenseEditorOpen}
        />
      </Card>

      {expensePilotEnabled && expenseEditorOpen && (
        <MonthlyExpenseFactsEditor
          year={year}
          rows={expenseRows}
          categories={expenseCategories}
          currency={expenseCurrency}
          initialMonth={year === currentYear ? new Date().getMonth() + 1 : 1}
          saving={expenseSaving}
          error={expenseError}
          onClose={() => setExpenseEditorOpen(false)}
          onSave={saveExpenseMonth}
        />
      )}
    </div>
  )
}
